import { Box } from "@chakra-ui/react";
import React from "react";
import {
  AlertIcon,
  ColumnsIcon,
  CornerDownRightIcon,
  GiftIcon,
  GlobeIcon,
  HeartIcon,
  LightningIcon,
  ListIcon,
  MailIcon,
  MinusCircleIcon,
  MobilePhoneIcon,
  MoneyIcon,
  PeopleIcon,
  PersonWithCheckIcon,
  PinIcon,
  RefreshIcon,
  SettingsIcon,
  SparklesIcon,
  TargetIcon,
  TrendIcon,
} from "ui/icons";
import hero from "../../../../public/assets/cdp-industries/heros/telecommunications.png";
import { dataBreachStat, personalizedExperiencesStat, revenueReferenceCopy, type CdpIndustryConfig } from "./shared";

export const telecommunicationsCdpConfig: CdpIndustryConfig = {
  icon: MobilePhoneIcon,
  name: "telecommunications",
  abbreviatedName: "telecom",
  pluralNoun: "telecom companies",
  color: "blue",
  seoCopy:
    "Leverage all your telecommunications data, including customers, accounts, service plans, usage records, devices, and support tickets.",
  heroImage: {
    src: hero,
    alt: "A photograph of a person holding a cell phone with a list of example data tables overlayed. The data tables included are for service plans, support tickets, devices, usage records, and accounts.",
  },
  adoptingCdpsDescription: (
    <>The telecommunication industry is incredibly competitive and complex. {revenueReferenceCopy}</>
  ),
  featuredOutcomes: [
    {
      icon: RefreshIcon,
      color: "yellow",
      title: "Reduce churn",
    },
    {
      icon: TrendIcon,
      color: "green",
      title: "Maximize LTV",
    },
    {
      icon: HeartIcon,
      color: "red",
      title: "Build loyalty",
    },
  ],
  dataStats: [
    {
      title: "$2.81T",
      description: (
        <>
          estimated market size <Box as="br" display={{ base: "block", lg: "none" }} />
          for telecom industry by 2033
        </>
      ),
      href: "https://www.thebrainyinsights.com/report/telecom-services-market-13622",
    },
    personalizedExperiencesStat,
    dataBreachStat,
  ],
  personalizationCards: [
    {
      title:
        "Verizon leverages over 1,500 data points per customer to personalize offers the moment someone enters a store",
      href: "https://www.reuters.com/technology/artificial-intelligence/verizon-uses-genai-improve-customer-loyalty-2024-06-18/?utm_source=chatgpt.com",
    },
    {
      title:
        "T-Mobile is partnering with OpenAI to create an AI Decisioning platform to deliver faster and more personalized customer experience",
      href: "https://www.t-mobile.com/news/business/t-mobile-launches-intentcx-with-openai",
    },
    {
      title: "Comcast is using AI to unlock more targeted and granular audience opportunities for advertisers",
      href: "https://builtin.com/articles/comcast-advertising-data-drives-more-meaningful-brand-consumer-connections",
    },
  ],
  useCases: {
    performanceMarketing: [
      // First row
      {
        icon: PersonWithCheckIcon,
        title: "Acquisition campaigns",
        description:
          "Drive new customer sign-ups by using demographic and usage intent data to target individuals and lookalike audiences searching for affordable mobile or internet plans, increasing subscriber growth.",
      },
      {
        icon: MinusCircleIcon,
        title: "Suppression campaigns",
        description:
          "Reduce wasted ad spend by suppressing current customers from acquisition campaigns using CRM data, reallocating budget toward competitive switching campaigns to improve ROAS.",
      },
      {
        icon: TargetIcon,
        title: "Retargeting campaigns",
        description:
          "Re-engage users who explored plans but didn’t convert by serving dynamic ads featuring their viewed plans and limited-time discounts, recovering lost leads and driving incremental subscriptions.",
      },
      // Second row
      {
        icon: MoneyIcon,
        title: "Cross-sell campaigns",
        description:
          "Increase ARPU by promoting complementary services such as streaming bundles, hotspot devices, or smart home integrations, driving premium service adoption.",
      },
      {
        icon: SettingsIcon,
        title: "Conversion APIs",
        description:
          "Share online and offline conversion data with ad platforms, enabling algorithms to better identify and target users with a higher likelihood of converting to improve campaign performance.",
      },
      {
        icon: GlobeIcon,
        title: "Media networks",
        description:
          "Monetize customer insights by offering anonymized audience segments to entertainment and app providers for targeted advertising, generating incremental revenue.",
      },
    ],
    lifecycleMarketing: [
      // First row
      {
        icon: MoneyIcon,
        title: "Upsell & cross-sell campaigns",
        description:
          "Recommend premium services such as unlimited data plans, high-speed internet, or streaming bundles to increase ARPU and customer retention.",
      },
      {
        icon: RefreshIcon,
        title: "Reactivation campaigns",
        description:
          "Re-engage inactive customers by offering device trade-ins, bill credits, or special discounts on returning plans.",
      },
      {
        icon: PeopleIcon,
        title: "Family plan engagement",
        description:
          "Encourage families to consolidate lines with bundled discounts, shared data plans, or free add-ons for additional lines.",
      },
      // Second row
      {
        icon: MobilePhoneIcon,
        title: "Device protection plan promotions",
        description:
          "Advertise device insurance, extended warranties, or phone replacement programs to boost recurring service revenue.",
      },
      {
        icon: PersonWithCheckIcon,
        title: "Win-back campaigns",
        description:
          "Offer waived activation fees, limited-time discounts, or bonus perks to encourage former customers to switch back.",
      },
      {
        icon: GiftIcon,
        title: "Anniversary campaigns",
        description: "Celebrate long-term customer loyalty with free bonus data, discounted upgrades, or bill credits.",
      },
    ],
    onsitePersonalization: [
      // First row
      {
        icon: LightningIcon,
        title: "Dynamic home page content",
        description:
          "Display tailored plans, add-ons, or discounts based on browsing and usage behavior to increase engagement and conversion rates.",
      },
      {
        icon: AlertIcon,
        title: "Proactive data usage alerts",
        description:
          "Notify users about approaching data caps and recommend upgrades or data add-ons to improve satisfaction and drive incremental revenue.",
      },
      {
        icon: CornerDownRightIcon,
        title: "Next best action",
        description:
          "Encourage actions like enrolling in autopay, saving billing information, or exploring loyalty rewards to boost engagement and retention.",
      },
      // Second row
      {
        icon: ColumnsIcon,
        title: "Interactive plan comparisons",
        description:
          "Provide side-by-side comparison tools to help customers evaluate plan options and benefits, accelerating decision-making and upgrades.",
      },
      {
        icon: MobilePhoneIcon,
        title: "Device trade-in evaluations",
        description:
          "Offer real-time trade-in assessments for existing devices during site visits to drive engagement and increase device upgrade revenue.",
      },
      {
        icon: SparklesIcon,
        title: "Gamified loyalty milestones",
        description:
          "Reward customers with points, badges, or tiers for activities like referrals, renewals, or on-time payments to deepen engagement and retention.",
      },
      // Third row
      {
        icon: PinIcon,
        title: "Localized 5G service notifications",
        description:
          "Notify users when 5G or faster internet services become available in their region to increase premium service adoption.",
      },
      {
        icon: ListIcon,
        title: "Real-time billing transparency",
        description:
          "Display detailed breakdowns of monthly bills, extra charges, or savings opportunities with plan changes to improve trust and reduce billing-related churn.",
      },
      {
        icon: MailIcon,
        title: "Upgrade & retention offers",
        description:
          "Dynamically present tailored retention incentives, plan upgrades, or exclusive discounts based on user tenure, data usage, and browsing behavior to increase customer lifetime value and reduce churn.",
      },
    ],
  },
  considerations: {
    dataModeling: {
      traditional: "Uses predefined models that may not fully capture usage trends or regional service preferences",
      composable: "Supports tailored models to handle customer usage data, billing records, and service preferences",
    },
    audienceManagement: {
      traditional:
        "Supports broad segmentation but struggles with identifying heavy data users or multi-line account behaviors",
      composable:
        "Enables granular segmentation using data like device usage patterns, family plans, and regional preferences",
    },
    customerJourneys: {
      traditional: "Provides standard templates for campaigns like contract renewals or upgrade offers",
      composable:
        "Powers fully adaptable journeys for events like service plan upgrades, device trade-ins, and churn prevention",
    },
    identityResolution: {
      traditional:
        "Relies on out-of-the-box algorithms that may not unify profiles across billing systems mobile apps and call logs",
      composable: "Supports custom algorithms to unify profiles across devices apps and regional service providers",
    },
  },
  whyHightouchTitleMaxW: "750px",
  audienceOverlap: {
    title: "High-intent plan switchers",
    firstCategoryLabel: "Abandoned plan upgrade process",
    secondCategoryLabel: "Mobile plan price checkers",
  },
};
